/**
 * The default board-node composer: BoardItem → scene BoxNode.
 *
 * One shared implementation of "what a module looks like" for every headless
 * consumer (server render endpoint, MCP render tool, app-shell SVG export) —
 * the module's widget props feed `moduleToCardInput`, and the composition is
 * scaled to the item's placed rect. Widget tags with a scene equivalent
 * (database cylinder, table) dispatch to their shape composition; everything
 * else renders as the standard module card. Callers with an icon set inject
 * `resolveIcon`.
 */
import { composeModuleCard } from "../card.js";
import {
  composeCylinderCard,
  composeTableCard,
  composeStatePill,
  composeStateMarker,
  composeNoteCard,
  composeClassCard,
  composeEnumCard,
  composePersonFigure,
  composeBoundaryFrame,
  SHAPE_ACCENTS,
  type ClassMember,
} from "../shapes.js";
import { composeFigureCard, FIGURE_SPECS } from "../figures.js";
import { defaultTokens, type Tokens } from "../tokens.js";
import { extractWidgetSpec, type WidgetSpec } from "../arch-model/widget-spec.js";
import { getFieldString } from "../arch-model/utils.js";
import type { Module } from "../arch-model/model.js";
import { moduleToCardInput, type ModuleToCardOptions } from "./card-input.js";
import type { ModuleCardInput } from "../card.js";
import type { BoardItem } from "./types.js";
import { applyLifecyclePaint, type LifecyclePaint } from "./lifecycle-style.js";
import type { BoxNode } from "../model.js";

/** The three pure derivations `composeBoardNode` computes from a module: its
 *  widget spec, the resolved card input, and the accent-scoped palette. The
 *  live custom element already derives all three for its host chrome, so it can
 *  hand them back here to avoid a second identical computation per render. */
export interface PrecomputedCard {
  readonly spec: WidgetSpec;
  readonly input: ModuleCardInput;
  readonly tokens: Tokens;
}

export interface ComposeBoardNodeOptions extends ModuleToCardOptions {
  /** Lifecycle paint for this node, applied AFTER shape dispatch so it reaches
   *  every composer uniformly (see `lifecycle-style.ts`).
   *
   *  Normally OMITTED: a space folded by `applyLifecycle` carries each item's
   *  paint on the item, and the composer picks it up from there. Pass it only
   *  when composing a single item outside a folded space (the live board, which
   *  holds the paint map itself). */
  readonly lifecycle?: LifecyclePaint;
  /** Live-board fast path: reuse spec/input/tokens the caller already derived
   *  (identical inputs → identical output, so the shared/export path is
   *  unaffected — it simply omits this). */
  readonly precomputed?: PrecomputedCard;
}

/** Tags whose composer projects the subspace portal itself (so a container of
 *  that type renders its children in place): the cylinder, the C4 boundary, and
 *  the external-system dashed card. The module-card family handles subspace via
 *  `input.subspace`, and an unknown/absent tag falls through to it — both need no
 *  entry here. Everything else is a leaf shape and defers to the card fallback. */
const SUBSPACE_CAPABLE_TAGS: ReadonlySet<string> = new Set([
  "arch-backend-database",
  "arch-c4-boundary",
  "arch-c4-external-system",
  "arch-external-system",
]);

/**
 * Compose a board node, then apply lifecycle paint.
 *
 * The dispatch below has a return per shape, so lifecycle is applied HERE
 * rather than inside each composer — one place to keep dashed/opacity/strike/
 * badge consistent across all eight shapes.
 *
 * Paint comes from the ITEM when the space has been folded by `applyLifecycle`,
 * which is what lets any backend render a folded space correctly without
 * knowing lifecycle exists. An explicit `opts.lifecycle` still wins, for the
 * live board, which composes item-by-item straight off its paint map.
 */
export function composeBoardNode(
  item: BoardItem,
  opts: ComposeBoardNodeOptions = {},
): BoxNode | null {
  const node = composeShape(item, opts);
  const lifecycle = opts.lifecycle ?? item.lifecycle;
  if (!lifecycle) return node;
  const mk = opts.makeTokens ?? defaultTokens;
  return applyLifecyclePaint(node, lifecycle, opts.precomputed?.tokens ?? mk(undefined));
}

function composeShape(
  item: BoardItem,
  opts: ComposeBoardNodeOptions = {},
): BoxNode | null {
  const m = item.meta as Module | undefined;
  if (!m) return null;
  const spec = opts.precomputed?.spec ?? extractWidgetSpec(m);
  // Subspace mode keys off the PLACED nested space (the same signal the live
  // board mirrors to `data-has-subspace`), not `module.children` — a partial
  // parse can synthesize phantom children. An explicit `opts.subspace` (the
  // live element passing `data-has-subspace`, whose fake item has no `.space`)
  // still wins.
  const subspace = opts.subspace ?? !!(item.space && item.space.items.length);
  const cardOpts = { ...opts, subspace };
  const input = opts.precomputed?.input ?? moduleToCardInput(m, spec.props, cardOpts);
  // Palette resolver — `defaultTokens` (headless) unless the caller injects a
  // theme-aware one (the live board reads the active data-theme CSS vars).
  const mk = opts.makeTokens ?? defaultTokens;
  const tokens = opts.precomputed?.tokens ?? mk(input.accent);
  const dims = { width: item.w, height: item.h };

  const tag = spec.tag;

  // Container safety net: a module WITH children must never silently drop them.
  // Only some composers project the subspace portal (the card family, the
  // cylinder, the C4 boundary). The figure / diagram composers (table, state,
  // note, class, enum, person, device figures) render leaf content only — if one
  // of those types is used as a container, fall back to the module card so its
  // nested space still shows, rather than vanishing (the same failure class as
  // the database-cylinder bug, latent for every leaf shape).
  if (subspace && tag && !SUBSPACE_CAPABLE_TAGS.has(tag)) {
    return composeModuleCard(input, tokens, dims);
  }

  if (tag === "arch-backend-database") {
    // Already resolved: `widget.classification: @@data.classification` was
    // evaluated in the engine, per node, so the prop is the value itself.
    const classRef = spec.props.get("classification");
    const classification = classRef != null ? String(classRef) : undefined;
    return composeCylinderCard(
      {
        name: input.name,
        subheader: input.subheader,
        accent: input.accent,
        badge: input.badge,
        badgeColor: input.badgeColor,
        classification: classification || undefined,
        subspace,
      },
      tokens,
      dims,
    );
  }
  if (tag === "arch-table") {
    return composeTableCard(
      {
        name: input.name,
        accent: input.accent,
        // Column detail is the SQL type from the `dtype` field (e.g. "uuid"),
        // not the interface's type name (which is just "column"). Falls back to
        // the type name for arch-table uses on non-column interfaces.
        rows: (m.interfaces ?? []).map((i) => ({ name: i.name, detail: getFieldString(i, "dtype") || i.typeName })),
      },
      tokens,
      dims,
    );
  }

  // Diagram / figure shapes bake their own accent (the live widget's `:host`
  // CSS). A COLOUR SIGNAL overrides it: an explicit `widget.color`/`accent`, OR
  // the board's unified node colour (aspect / label / diff), which arrives as
  // `accentOverride` and is already folded into `input.accent`. Without a
  // signal, the baked default is used. Returns `undefined` for shapes whose
  // composer picks its own default (person figure) when no signal is present.
  const explicitColor = spec.props.has("color") || spec.props.has("accent");
  const hasColorSignal = explicitColor || opts.accentOverride != null;
  function shapeAccent(fallback: string): string;
  function shapeAccent(fallback: undefined): string | undefined;
  function shapeAccent(fallback: string | undefined): string | undefined {
    return hasColorSignal ? input.accent : fallback;
  }

  if (tag === "arch-state") {
    return composeStatePill(
      { name: input.name, accent: shapeAccent(SHAPE_ACCENTS.state) },
      mk(shapeAccent(SHAPE_ACCENTS.state)),
      dims,
    );
  }
  if (tag === "arch-state-initial" || tag === "arch-state-final") {
    return composeStateMarker(tag === "arch-state-initial" ? "initial" : "final", tokens, dims);
  }
  if (tag === "arch-note") {
    return composeNoteCard(
      { name: input.name, description: m.description, accent: shapeAccent(SHAPE_ACCENTS.note) },
      mk(shapeAccent(SHAPE_ACCENTS.note)),
      dims,
    );
  }
  if (tag === "arch-class") {
    // Partition interfaces into attributes (those with a `dtype` field) then
    // operations (those without), each keeping declaration order — mirrors the
    // live arch-class widget's `partitionClassMembers`.
    const attrs: ClassMember[] = [];
    const ops: ClassMember[] = [];
    for (const iface of m.interfaces ?? []) {
      const dtype = getFieldString(iface, "dtype");
      if (dtype != null && dtype !== "") {
        attrs.push({ name: iface.name, ann: dtype, op: false });
      } else {
        ops.push({ name: iface.name, ann: getFieldString(iface, "returns") ?? getFieldString(iface, "ret") ?? "", op: true });
      }
    }
    const members = attrs.concat(ops);
    return composeClassCard(
      { name: input.name, members, accent: shapeAccent(SHAPE_ACCENTS.class) },
      mk(shapeAccent(SHAPE_ACCENTS.class)),
      dims,
    );
  }
  if (tag === "arch-enum") {
    const rows = (m.interfaces ?? []).map((iface) => ({
      name: iface.name,
      value: getFieldString(iface, "value"),
    }));
    return composeEnumCard(
      { name: input.name, rows, accent: shapeAccent(SHAPE_ACCENTS.enum) },
      mk(shapeAccent(SHAPE_ACCENTS.enum)),
      dims,
    );
  }

  // ── C4 model ──
  if (tag === "arch-c4-person" || tag === "arch-c4-external-person") {
    const external = tag === "arch-c4-external-person";
    // The figure bakes its internal/external accent; a colour signal overrides.
    return composePersonFigure(
      { name: input.name, external, accent: shapeAccent(undefined) },
      tokens,
      dims,
    );
  }
  if (tag === "arch-c4-boundary") {
    const boundaryAccent = shapeAccent(SHAPE_ACCENTS.boundary);
    return composeBoundaryFrame(
      { name: input.name, scope: spec.props.get("scope") != null ? String(spec.props.get("scope")) : undefined, accent: boundaryAccent },
      mk(boundaryAccent),
      dims,
    );
  }
  if (tag === "arch-c4-external-system" || tag === "arch-external-system") {
    // A dashed module card (SaaS / third-party dependency). input.accent already
    // carries any widget.color / aspect-diff override. The backend
    // `arch-external-system` keeps rendering archui on the LIVE board (its
    // vendor/domain detail rows have no scene-card slot); this only shapes the
    // headless export, where the dashed frame still marks it as external.
    return composeModuleCard({ ...input, dashed: true }, tokens, dims);
  }

  // ── arch.extras device/actor figures (illustration glyph + caption) ──
  if (tag && FIGURE_SPECS[tag]) {
    // Sub-line: an EXPLICIT `widget.subheader` path-ref that RESOLVES wins, else
    // the literal `widget.caption` — the legacy ArchUIFigure
    // `resolveRef("subheader") || attr("caption")` precedence (falsy → fall
    // through). Require both the raw prop AND a resolved value: the prop guard
    // stops the card composer's type-derived subheader default (e.g. "cloud")
    // from shadowing the caption; the value check lets an empty subheader ref
    // still fall back to the caption instead of dropping the sub-line entirely.
    const capProp = spec.props.get("caption");
    const caption = (spec.props.has("subheader") && input.subheader)
      ? input.subheader
      : capProp != null ? String(capProp) : undefined;
    return composeFigureCard(tag, input.name, tokens, dims, shapeAccent(undefined), caption);
  }

  return composeModuleCard(input, tokens, dims);
}
